import React, { useEffect, useState } from 'react';
import { css } from 'styled-components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import options from '../../../../../helpers/ContentFulOptions';
import Button from '@vfuk/core-button';
import {
  KOA_API_ENDPOINT,
  OPCO_CODE,
  LANG_CODE,
} from '../SectionAssessmentSurvey/SectionAssessmentBusinessGoals/SectionAssessmentBusinessGoals';
import runSeoEventData from '../../../../../helpers/runSeoData';
import base64ToBlob from '../../../../../helpers/base64ToBlob';
import {
  buttonCSS,
  textOverridesForBanners,
  outerCSS,
  heroOuterCSS,
  wrapperCSS,
  innerCSS,
  formCSS,
} from '../css/DownloadBannerStyles';
import { mocksUrls } from '../__mocks__/AssesssmentService.mocks';
import {
  SummaryDownloadBannerProps,
  CustomWindow,
} from '../Types/SummaryDownloadBannerTypes';
import twinxClient from '../../SectionSmartSearch/ProtectedApiClient';
import TextInputWithLabel from '@vfuk/core-text-input-with-label';
import fireButtonAndLinkClicksEvent from '../../../../../helpers/fireButtonAndLinkClicksEvent';

const onAssessmentSummarySuccessfulDownload = () => {
  try {
    // fire the utag link event
    if ((window as CustomWindow).onAssessmentSummarySuccessfulDownload) {
      (window as CustomWindow).onAssessmentSummarySuccessfulDownload!();
    }
  } catch (e) {
    console.log(
      'onAssessmentSummarySuccessfulDownload failed - please check the Assessment Summary Successful Download Script'
    );
  }
};

type ImageKeys = string | number;

const SummaryDownloadBanner = ({
  textPosition,
  header,
  headerColor,
  headerTextAlign,
  nameInput,
  emailInput,
  privacyText,
  privacyColor,
  privacyTextAlign,
  button,
  responsiveImageCollection = [],
  emailSurveyId,
}: SummaryDownloadBannerProps) => {
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const [btndisable, setbtnDisable] = useState<boolean>(true);

  const theHeroCSS = outerCSS[textPosition];
  const theHeroOuterCSS = heroOuterCSS[textPosition];
  const theContentWrapper = wrapperCSS[textPosition];
  const theContentCSS = innerCSS[textPosition];

  let headerParsedRaw, privacyParsedRaw;
  let bgAltText = '';
  let bannerJSX = null;

  const images = responsiveImageCollection.map(respImage => {
    const container: Record<ImageKeys, any> = {};
    container['source'] = respImage.imageTitle.file.url;
    container['maxWidth'] = respImage.maxWidth;
    container['secondImageSource'] = respImage.secondImage?.file?.url ?? null;
    return container;
  });

  images.sort((a, b) => (b.maxWidth > a.maxWidth && 1) || -1);
  const bgImages = images.reduce((acc, css, idx) => {
    if (idx === 0) {
      return `background-image: url("${css.source}");`;
    }
    return `${acc}
        @media (max-width: ${css.maxWidth}px) {
          & {
            background-image: url("${css.source}");
            }
        };`;
  }, '');

  const headerTextCSS = css`
    color: ${headerColor};
    text-align: ${headerTextAlign};
    h3 {
      color: ${headerColor};
    }
    h5 {
      color: ${headerColor};
    }
  `;

  const privacyTextCSS = css`
    p {
      font-size: 10px;
      font-family: 'Vodafone', sans-serif;
      color: ${privacyColor};
      text-align: ${privacyTextAlign};
      line-height: 11px;
      margin-bottom: 1.5em;
    }
  `;

  const validate = () => {
    if (name !== '' && email !== '' && isValidEmail(email)) {
      setbtnDisable(false);
    } else {
      setbtnDisable(true);
    }
  };

  useEffect(() => {
    validate();
  }, [name]);

  useEffect(() => {
    validate();
  }, [email]);

  const isValidEmail = (email: any) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const emailPatchAPI = async () => {
    if (name === '' || email === '' || !isValidEmail(email)) {
      alert('Please type name and email');
      return false;
    }
    setSending(true);
    fireButtonAndLinkClicksEvent({
      event_action: 'button click',
      event_label: button.label,
    });
    const query: any = {
      Opco: OPCO_CODE,
      Language: LANG_CODE,
      surveyid: emailSurveyId,
      get: 'recommendation',
    };
    const params = new URLSearchParams(query).toString();
    const body = {
      relatedParty: [
        {
          name: email,
          role: 'email',
        },
      ],
    };
    const url = KOA_API_ENDPOINT
      ? `${KOA_API_ENDPOINT}/assessment?${params}`
      : mocksUrls.SumDowBannerUrl;
    try {
      const { data } = await twinxClient.patch(url, body);
      if (!data?.result?.hasOwnProperty('code')) {
        runSeoEventData(button.seoEventData.seoEventData);
        setName('');
        setEmail('');
        setSending(false);
        setbtnDisable(true);
        if (data?.result?.hasOwnProperty('attachment')) {
          const base64PDF = data?.result?.attachment[0]?.content;
          const linkSource = `data:application/pdf;base64,${base64PDF}`;
          const downloadLink = document.createElement('a');
          const fileName = 'V-Hub_Recommendation.pdf';
          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          downloadLink.click();
          const base64 = base64PDF;
          const blob = base64ToBlob(base64, 'application/pdf');
          const objectUrl = URL.createObjectURL(blob);
          const pdfWindow: any = window.open('');
          pdfWindow.document.write(
            "<iframe frameBorder='0' width='100%' height='100%' src='" +
              objectUrl +
              "'></iframe>"
          );
          onAssessmentSummarySuccessfulDownload();
        }
      } else {
        console.log('Something went wrong try again');
        alert('Something went wrong try again');
        setSending(false);
      }
    } catch (error) {
      console.log(error);
      if (!window.navigator.onLine) {
        alert(
          'It looks like you have no internet connection, please refresh the page and try again...'
        );
      } else if (error.name === 'TypeError') {
        alert('Please enable the popup for the site and try again');
      } else {
        alert('Something went wrong try again');
      }
      setSending(false);
    }
  };

  responsiveImageCollection.some(imageData => {
    const isFound = imageData.imageTitle.description !== '';
    if (isFound) {
      bgAltText = imageData.imageTitle.description as string;
    }
    return isFound;
  });

  if (header) {
    headerParsedRaw = documentToReactComponents(JSON.parse(header), options);
  }
  if (privacyText) {
    privacyParsedRaw = documentToReactComponents(
      JSON.parse(privacyText),
      options
    );
  }

  bannerJSX = (
    <div
      role="img"
      aria-label={bgAltText}
      css={[theHeroOuterCSS, textOverridesForBanners, bgImages]}
    >
      <div css={[theHeroCSS, theContentWrapper]}>
        <div css={theContentCSS}>
          <div className="RichText" css={headerTextCSS}>
            {headerParsedRaw}
          </div>
          <form>
            <div css={formCSS}>
              <TextInputWithLabel
                fieldWrapper={{
                  label: `${nameInput.label}*`,
                  stateText: 'Something is wrong',
                }}
                textInput={{
                  dataAttributes: { 'data-testid': 'testBox1' },
                  type: 'text',
                  value: name,
                  onChange: (e: any) => {
                    setName(e.target.value);
                  },
                  placeholder: nameInput.placeholder,
                  id: 'testBox1',
                  name: 'name',
                  required: nameInput.required,
                }}
              />
            </div>
            <div css={formCSS}>
              <TextInputWithLabel
                fieldWrapper={{
                  label: `${emailInput.label}*`,
                  stateText: 'Something is wrong',
                }}
                textInput={{
                  dataAttributes: { 'data-testid': 'testBox2' },
                  type: 'email',
                  value: email,
                  onChange: (e: any) => {
                    setEmail(e.target.value);
                  },
                  placeholder: emailInput.placeholder,
                  id: 'testBox2',
                  name: 'email',
                  required: emailInput.required,
                }}
              />
            </div>
            <div css={privacyTextCSS}>{privacyParsedRaw}</div>
          </form>
          <div css={buttonCSS}>
            <Button
              dataAttributes={{ 'data-testid': 'emailPatchId' }}
              text={button.label}
              appearance={btndisable ? 'secondary' : 'alt1'}
              inverse
              loading={sending}
              state={btndisable ? 'disabled' : undefined}
              onClick={emailPatchAPI}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return bannerJSX;
};
export default SummaryDownloadBanner;
